import { useMemo, useState } from "react";
import { Search, SlidersHorizontal } from "lucide-react";
import { Badge } from "ui/badge";
import { Card, CardContent } from "ui/card";
import { Input } from "ui/input";
import Pagination from "ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "ui/select";

type FleetSource = "internal" | "regular-vendor" | "external-vendor";
type FleetStatus = "available" | "assigned" | "in-route" | "maintenance";
type Vehicle = {
  id: string;
  registration: string;
  type: string;
  capacity: number;
  source: FleetSource;
  vendor: string;
  location: string;
  status: FleetStatus;
  driverId?: string;
  nextMovement?: string;
};

type Driver = {
  id: string;
  name: string;
  employeeNo: string;
  maxHours: number;
  usedHours: number;
  maxShifts: number;
  usedShifts: number;
  license: string;
  status: "available" | "on-duty" | "rest";
};

type AvailableVehicleProps = {
  vehicles: Vehicle[];
  drivers: Driver[];
  selectedMovementIds: string[];
  onAssignSelectedTransfers: (vehicleId: string) => void;
  draggedVehicleId: string;
  setDraggedVehicleId: React.Dispatch<React.SetStateAction<string>>;
};

export function AvailableVehicle({
  vehicles,
  drivers,
  selectedMovementIds,
  onAssignSelectedTransfers,
  draggedVehicleId,
  setDraggedVehicleId,
}: AvailableVehicleProps) {
  const [source, setSource] = useState("all");
  const [status, setStatus] = useState("available");
  const [statusFilter, setStatusFilter] = useState("all");
  const [type, setType] = useState("all");
  const [search, setSearch] = useState("");
  const [vehiclePage, setVehiclePage] = useState(1);
  const [vehiclePageSize, setVehiclePageSize] = useState(10);

  const filteredVehicles = useMemo(
    () =>
      vehicles.filter((vehicle) => {
        if (source !== "all" && vehicle.source !== source) return false;

        const activeStatus = statusFilter !== "all" ? statusFilter : status;

        // Available tab should also show assigned vehicles
        if (activeStatus === "available") {
          if (
            vehicle.status !== "available" &&
            vehicle.status !== "assigned"
          ) {
            return false;
          }
        } else if (
          activeStatus !== "all" &&
          vehicle.status !== activeStatus
        ) {
          return false;
        }

        if (type !== "all" && vehicle.type !== type) return false;

        const query = search.trim().toLowerCase();

        return (
          !query ||
          [
            vehicle.registration,
            vehicle.type,
            vehicle.vendor,
            vehicle.location,
          ].some((value) => value.toLowerCase().includes(query))
        );
      }),
    [search, source, status, statusFilter, type, vehicles],
  );

  const paginatedVehicles = filteredVehicles.slice(
    (vehiclePage - 1) * vehiclePageSize,
    vehiclePage * vehiclePageSize,
  );

  const sourceLabel: Record<FleetSource, string> = {
    internal: "Internal",
    "regular-vendor": "Vendor",
    "external-vendor": "External",
  };

  const sourceClass: Record<FleetSource, string> = {
    internal: "border-sky-200 bg-sky-50 text-sky-700",
    "regular-vendor": "border-violet-200 bg-violet-50 text-violet-700",
    "external-vendor": "border-amber-200 bg-amber-50 text-amber-700",
  };

  const statusClass: Record<FleetStatus, string> = {
    available: "bg-emerald-500",
    assigned: "bg-sky-500",
    "in-route": "bg-violet-500",
    maintenance: "bg-orange-500",
  };

  return (
    <div className="order-1 flex h-full flex-col gap-3 mt-4">
      <>
        <div className="grid grid-cols-3 gap-2 rounded-xl border border-slate-200 bg-white p-2 shadow-sm">
          {[
            [
              "available",
              "Available Vehicles",
              vehicles.filter(
                (item) =>
                  item.status === "available" ||
                  item.status === "assigned"
              ).length,
            ],
            [
              "maintenance",
              "Maintenance",
              vehicles.filter((item) => item.status === "maintenance").length,
            ],
          ].map(([value, label, count]) => (
            <button
              key={String(value)}
              type="button"
              onClick={() => {
                const selectedStatus = String(value);

                setStatus(selectedStatus);
                setStatusFilter("all");
                setSource("all");
                setVehiclePage(1);
              }}
              className={`flex h-8 min-w-0 items-center justify-between gap-1 rounded-lg border px-3 text-left transition ${
                status === value
                  ? "border-sky-600 bg-sky-600 text-white"
                  : "border-slate-200 bg-white text-slate-600 hover:border-sky-200 hover:bg-sky-50"
              }`}
            >
              <div className="truncate text-sm font-semibold">{label}</div>

              <div
                className={`shrink-0 text-sm font-bold ${
                  status === value ? "text-white" : "text-slate-900"
                }`}
              >
                {count}
              </div>
            </button>
          ))}
        </div>
        <Card
          className="flex-1 overflow-hidden rounded-xl border-slate-200 bg-white shadow-sm"
          style={{ marginTop: "0px", gap: "0px" }}
        >
          <CardContent
            className="flex h-full flex-col"
            style={{ padding: "0px" }}
          >
            <div className="flex min-w-0 items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2.5">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-sky-100 text-sky-700">
                <SlidersHorizontal className="h-4 w-4" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-slate-900">
                  Available Vehicles
                </div>
                <div className="truncate text-xs text-slate-500">
                  View up to 10 vehicles and drop selected transfer lines onto a
                  vehicle
                </div>
              </div>
            </div>
            <div className="grid grid-cols-3 items-center gap-2 border-b border-slate-200 bg-slate-50/70 px-3 py-2">
              <div className="relative min-w-0">
                <Search className="absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-slate-400" />
                <Input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search vehicles…"
                  className="h-7 w-full pl-7 text-[9px]"
                />
              </div>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger className="h-7 w-full whitespace-nowrap px-2 text-[8px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {Array.from(new Set(vehicles.map((item) => item.type))).map(
                    (item) => (
                      <SelectItem key={item} value={item}>
                        {item}
                      </SelectItem>
                    ),
                  )}
                </SelectContent>
              </Select>
              <Select
                value={statusFilter}
                onValueChange={(value) => {
                  setStatusFilter(value);
                  setVehiclePage(1);
                }}
              >
                <SelectTrigger className="h-7 w-full whitespace-nowrap px-2 text-[8px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="available">Available</SelectItem>
                  <SelectItem value="in-route">In Route</SelectItem>
                  <SelectItem value="maintenance">Maintenance</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid flex-1 grid-cols-1 gap-2 bg-slate-50/50 p-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
              {paginatedVehicles.map((vehicle) => {
                const driver = drivers.find(
                  (item) => item.id === vehicle.driverId,
                );
                const canDrag = vehicle.status === "available";
                return (
                  <Card
                    key={vehicle.id}
                    draggable={canDrag}
                    onDragOver={(event) => {
                      if (
                        selectedMovementIds.length &&
                        vehicle.status !== "maintenance"
                      )
                        event.preventDefault();
                    }}
                    onDrop={(event) => {
                      event.preventDefault();
                      if (selectedMovementIds.length)
                        onAssignSelectedTransfers(vehicle.id);
                    }}
                    onDragStart={(event) => {
                      event.dataTransfer.effectAllowed = "copy";
                      event.dataTransfer.setData("text/plain", vehicle.id);
                      setDraggedVehicleId(vehicle.id);
                    }}
                    onDragEnd={() => setDraggedVehicleId("")}
                    className={`relative h-[76px] shrink-0 gap-0 overflow-hidden rounded-lg border border-slate-200 bg-white py-0 shadow-none transition hover:border-sky-200 hover:shadow-sm ${canDrag ? "cursor-grab active:cursor-grabbing" : "opacity-75"}`}
                    style={{ marginTop: "0px" }}
                  >
                    <div
                      className={`absolute inset-y-1.5 left-0 w-0.5 ${statusClass[vehicle.status]}`}
                    />
                    <CardContent
                      className="h-full overflow-hidden"
                      style={{ padding: "8px 16px" }}
                    >
                      <div className="flex min-w-0 items-center gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <span className="text-sm font-bold text-slate-900">
                              {vehicle.registration}
                            </span>
                            <span className="text-xs text-gray-600">
                              {vehicle.type} · {sourceLabel[vehicle.source]}
                            </span>
                          </div>
                          <div
                            className="mt-0.5 truncate text-xs text-gray-500"
                            title={`${vehicle.vendor} · ${vehicle.location}`}
                          >
                            {vehicle.status === "available"
                              ? vehicle.location
                              : `${vehicle.vendor} · ${vehicle.location}`}
                          </div>
                          <div className="mt-0.5 flex gap-2 text-xs text-gray-500">
                            <span>
                              {vehicle.status === "maintenance"
                                ? driver?.name || vehicle.vendor
                                : "No active assignment"}
                            </span>
                            {vehicle.nextMovement && (
                              <span className="truncate">
                                {vehicle.nextMovement}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex shrink-0 flex-col items-end">
                          <Badge
                            className={`px-1 py-0 text-[7px] ${sourceClass[vehicle.source]}`}
                          >
                            {sourceLabel[vehicle.source]}
                          </Badge>
                          <span
                            className={`mt-0.5 text-[11px] font-semibold ${
                              vehicle.status === "maintenance"
                                ? "text-orange-700"
                                : vehicle.status === "assigned"
                                  ? "text-red-600"
                                  : vehicle.status === "available"
                                    ? "text-emerald-700"
                                    : "text-sky-700"
                            }`}
                          >
                            {vehicle.status === "assigned" || vehicle.status === "available"
                              ? "Available"
                              : vehicle.status.replace("-", " ")}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
              {!filteredVehicles.length && (
                <div className="col-span-full rounded-md border border-dashed p-8 text-center text-xs text-slate-500">
                  No vehicles match the selected filters.
                </div>
              )}
            </div>
          </CardContent>
          <div className="col-span-full rounded-lg bg-white">
            <div className="col-span-full rounded-lg bg-white">
              <Pagination
                totalItems={filteredVehicles.length}
                itemsPerPage={vehiclePageSize}
                currentPage={vehiclePage}
                onPageChange={setVehiclePage}
                onPageSizeChange={(size) => {
                  setVehiclePageSize(size);
                  setVehiclePage(1);
                }}
                label="vehicles"
              />
            </div>
          </div>
        </Card>
      </>
    </div>
  );
}
